/**
 * Native Bridge for WebView Communication
 * Handles communication between web app and native Flutter app
 */

// Detect if running in custom web view
export const isInWebView = () =>
  /MadnessTime|flutter|wv/.test(navigator.userAgent);

// Generate unique request IDs
const generateRequestId = () =>
  `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Store pending requests
const pendingRequests = new Map();

// PostMessage Bridge for Native Communication
export class NativeBridge {
  constructor() {
    this.isWebView = isInWebView();
    this.setupMessageListener();
  }

  setupMessageListener() {
    if (!this.isWebView) return;

    window.addEventListener('message', (event) => {
      try {
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.type === 'location_response') {
          const requestId = data.requestId;
          const request = pendingRequests.get(requestId);

          if (request) {
            pendingRequests.delete(requestId);

            if (data.success && data.location) {
              request.resolve(data.location);
            } else {
              request.reject(
                new Error(data.error || 'Location request failed')
              );
            }
          }
        }
      } catch (error) {
        console.debug('NativeBridge: Error parsing message:', error);
      }
    });
  }

  // Request location from native app
  async getLocation() {
    if (!this.isWebView) {
      // Fallback to browser geolocation for regular web
      return this.getBrowserLocation();
    }

    return new Promise((resolve, reject) => {
      const requestId = generateRequestId();

      // Store the request
      pendingRequests.set(requestId, { resolve, reject });

      // Set timeout
      const timeout = setTimeout(() => {
        pendingRequests.delete(requestId);
        reject(new Error('Location request timeout'));
      }, 10000); // 10 second timeout

      // Send request to native app
      window.postMessage(
        {
          type: 'location_request',
          requestId,
          timestamp: Date.now(),
        },
        '*'
      );

      // Clean up timeout on success
      const originalResolve = resolve;
      resolve = (location) => {
        clearTimeout(timeout);
        originalResolve(location);
      };
    });
  }

  // Fallback browser location
  async getBrowserLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
            source: 'browser',
          });
        },
        (error) => {
          reject(new Error(`Location error: ${error.message}`));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  }

  // Check if location services are available
  async isLocationAvailable() {
    if (!this.isWebView) {
      return !!navigator.geolocation;
    }

    try {
      await this.getLocation();
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Singleton instance
export const nativeBridge = new NativeBridge();

// Helper function for components
export const getLocationWithFallback = async () => {
  try {
    const location = await nativeBridge.getLocation();
    return { success: true, location };
  } catch (error) {
    console.warn('Location unavailable:', error.message);
    return { success: false, error: error.message };
  }
};
